import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Plus, Eye, Pencil, Trash2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import ExternalPaddingControl from '../../components/admin/ExternalPaddingControl';
import api from '../../lib/api';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';
import HeaderBlock from '../../components/blocks/HeaderBlock';
import MenuBlock from '../../components/blocks/MenuBlock';
import FooterBlock from '../../components/blocks/FooterBlock';

/**
 * AdminGlobalBlocks - Interface graphique complète pour gérer les blocs globaux
 * 
 * Fonctionnalités :
 * - Formulaires visuels (pas de JSON brut)
 * - Preview en temps réel
 * - Templates prédéfinis
 * - Responsive (desktop + mobile)
 * - Validation automatique
 */
const AdminGlobalBlocks = () => {
  const { toast } = useToast();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [previewBlock, setPreviewBlock] = useState(null);
  
  // Formulaire création
  const [createForm, setCreateForm] = useState({
    type: 'header',
    template: 'complet',
    title: ''
  });

  // État des sections (collapsible mobile)
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    menu: true,
    footer: true
  });

  // Templates prédéfinis
  const TEMPLATES = {
    header: {
      simple: {
        title: 'Header Simple',
        content: {
          site_name: 'Games Visor',
          logo_url: '',
          show_search: false,
          show_login: true,
          show_signup: false
        }
      },
      complet: {
        title: 'Header Complet',
        content: {
          site_name: 'Games Visor',
          logo_url: '',
          show_search: true,
          show_login: true,
          show_signup: true
        }
      }
    },
    menu: {
      standard: {
        title: 'Menu Standard',
        content: {
          style: 'horizontal',
          show_icons: false
        }
      }
    },
    footer: {
      minimal: {
        title: 'Footer Minimal',
        content: {
          site_name: 'Games Visor',
          logo_url: '',
          tagline: 'La Plateforme pour les Professionnels du Monde Ludique',
          email: '',
          phone: '',
          address: '',
          legal_links: [
            { label: 'Mentions Légales', url: '/mentions-legales' }
          ]
        }
      },
      riche: {
        title: 'Footer Riche',
        content: {
          site_name: 'Games Visor',
          logo_url: '',
          tagline: 'La Plateforme pour les Professionnels du Monde Ludique',
          email: 'contact@gamesvisor.com',
          phone: '+33 1 23 45 67 89',
          address: 'Paris, France',
          legal_links: [
            { label: 'Mentions Légales', url: '/mentions-legales' },
            { label: 'CGV', url: '/cgv' },
            { label: 'Politique de Confidentialité', url: '/confidentialite' }
          ],
          social_links: []
        }
      }
    },
    thematic_page_card: {
      couleur: {
        title: 'Carte avec Couleur',
        content: {
          label: 'Carte Thématique',
          title: 'Pré-commandes',
          description: 'Découvrez nos jeux en pré-commande',
          background_type: 'color',
          background_color: '#6366f1',
          background_image_url: '',
          target_page_id: '',
          button_text: 'Découvrir',
          custom_link: '/dashboard/precommandes'
        }
      },
      image: {
        title: 'Carte avec Image',
        content: {
          label: 'Carte Thématique',
          title: 'Nouveautés',
          description: 'Découvrez les dernières nouveautés',
          background_type: 'image',
          background_color: '#6366f1',
          background_image_url: 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=800',
          target_page_id: '',
          button_text: 'Explorer',
          custom_link: '/dashboard/nouveautes'
        }
      }
    }
  };

  useEffect(() => {
    loadBlocks();
  }, []);

  const loadBlocks = async () => {
    try {
      const response = await api.get('/global-blocks');
      setBlocks(response.data);
    } catch (error) {
      console.error('Erreur chargement blocs:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les blocs globaux',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Créer depuis template
  const handleCreateFromTemplate = async () => {
    const template = TEMPLATES[createForm.type][createForm.template];
    try {
      const newBlock = {
        type: createForm.type,
        title: createForm.title || template.title,
        content: template.content,
        is_active: true
      };
      
      const response = await api.post('/global-blocks', newBlock);
      setBlocks([...blocks, response.data]);
      setShowCreateDialog(false);
      setCreateForm({ type: 'header', template: 'complet', title: '' });
      
      toast({
        title: 'Succès',
        description: `Bloc ${createForm.type} créé avec succès`
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur création bloc',
        variant: 'destructive'
      });
    }
  };

  // Éditer un bloc
  const handleEdit = (block) => {
    setEditingBlock({ ...block });
    setShowEditDialog(true);
  };

  // Sauvegarder modifications
  const handleSave = async () => {
    try {
      const response = await api.put(`/global-blocks/${editingBlock.id}`, {
        title: editingBlock.title,
        content: editingBlock.content,
        is_active: editingBlock.is_active
      });
      
      setBlocks(blocks.map(b => b.id === response.data.id ? response.data : b));
      setShowEditDialog(false);
      setEditingBlock(null);
      
      toast({
        title: 'Succès',
        description: 'Bloc mis à jour'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur sauvegarde',
        variant: 'destructive'
      });
    }
  };

  // Supprimer
  const [deleteTarget, setDeleteTarget] = useState(null);

  const requestDelete = (blockId) => {
    const block = blocks.find(b => b.id === blockId);
    if (block) setDeleteTarget(block);
  };

  const handleDelete = async (blockIdOrNull) => {
    const targetId = blockIdOrNull || deleteTarget?.id;
    if (!targetId) return;
    try {
      await api.delete(`/global-blocks/${targetId}`);
      setBlocks(blocks.filter(b => b.id !== targetId));
      setDeleteTarget(null);
      toast({
        title: 'Succès',
        description: 'Bloc supprimé'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur suppression',
        variant: 'destructive'
      });
    }
  };

  // Toggle actif/inactif
  const toggleActive = async (block) => {
    try {
      const response = await api.put(`/global-blocks/${block.id}`, {
        is_active: !block.is_active
      });
      
      setBlocks(blocks.map(b => b.id === response.data.id ? response.data : b));
      
      toast({
        title: 'Succès',
        description: `Bloc ${response.data.is_active ? 'activé' : 'désactivé'}`
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur',
        variant: 'destructive'
      });
    }
  };

  // Preview
  const handlePreview = (block) => {
    setPreviewBlock(block);
    setShowPreviewDialog(true);
  };

  const getBlocksByType = (type) => blocks.filter(b => b.type === type);

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Rendu preview
  const renderPreview = (block) => {
    switch (block.type) {
      case 'header':
        return <HeaderBlock block={block} />;
      case 'menu':
        return <MenuBlock block={block} />;
      case 'footer':
        return <FooterBlock block={block} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Blocs Globaux</h1>
          <p className="text-muted-foreground mt-1">
            Gérez les éléments communs de votre site (Header, Menu, Footer)
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Créer un Bloc
        </Button>
      </div>

      {/* Section Header */}
      <section className="border rounded-lg bg-white">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSection('header')}
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            🎨 Header
            <span className="text-sm text-muted-foreground">
              ({getBlocksByType('header').length})
            </span>
          </h2>
          {expandedSections.header ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
        
        {expandedSections.header && (
          <div className="p-4 border-t space-y-4">
            {getBlocksByType('header').length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun bloc Header. Créez-en un pour commencer.
              </p>
            ) : (
              getBlocksByType('header').map(block => (
                <BlockCard
                  key={block.id}
                  block={block}
                  onEdit={handleEdit}
                  onDelete={requestDelete}
                  onToggleActive={toggleActive}
                  onPreview={handlePreview}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* Section Menu */}
      <section className="border rounded-lg bg-white">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSection('menu')}
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            📋 Menu
            <span className="text-sm text-muted-foreground">
              ({getBlocksByType('menu').length})
            </span>
          </h2>
          {expandedSections.menu ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
        
        {expandedSections.menu && (
          <div className="p-4 border-t space-y-4">
            {getBlocksByType('menu').length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun bloc Menu. Créez-en un pour commencer.
              </p>
            ) : (
              getBlocksByType('menu').map(block => (
                <BlockCard
                  key={block.id}
                  block={block}
                  onEdit={handleEdit}
                  onDelete={requestDelete}
                  onToggleActive={toggleActive}
                  onPreview={handlePreview}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* Section Footer */}
      <section className="border rounded-lg bg-white">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSection('footer')}
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            📄 Footer
            <span className="text-sm text-muted-foreground">
              ({getBlocksByType('footer').length})
            </span>
          </h2>
          {expandedSections.footer ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
        
        {expandedSections.footer && (
          <div className="p-4 border-t space-y-4">
            {getBlocksByType('footer').length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun bloc Footer. Créez-en un pour commencer.
              </p>
            ) : (
              getBlocksByType('footer').map(block => (
                <BlockCard
                  key={block.id}
                  block={block}
                  onEdit={handleEdit}
                  onDelete={requestDelete}
                  onToggleActive={toggleActive}
                  onPreview={handlePreview}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* Dialog Création */}
      <CreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        form={createForm}
        setForm={setCreateForm}
        templates={TEMPLATES}
        onCreate={handleCreateFromTemplate}
      />

      {/* Dialog Édition */}
      <EditDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        block={editingBlock}
        setBlock={setEditingBlock}
        onSave={handleSave}
      />

      {/* Dialog Preview */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu : {previewBlock?.title}</DialogTitle>
            <DialogDescription>
              Voici comment le bloc apparaîtra sur le site
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-lg overflow-hidden">
            {previewBlock && renderPreview(previewBlock)}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DestructiveActionDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Supprimer le bloc global"
        itemName={deleteTarget?.title || deleteTarget?.type || ''}
        description="Le bloc global sera définitivement supprimé de toutes les pages où il est utilisé."
        onConfirm={() => handleDelete()}
      />
    </div>
    </DashboardLayout>
  );
};

// Composant Card pour chaque bloc
const BlockCard = ({ block, onEdit, onDelete, onToggleActive, onPreview }) => {
  return (
    <div className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold">{block.title}</h3>
          <span className={`px-2 py-1 rounded-full text-xs ${
            block.is_active 
              ? 'bg-green-100 text-green-700' 
              : 'bg-gray-100 text-gray-700'
          }`}>
            {block.is_active ? '✅ Actif' : '⭕ Inactif'}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 capitalize">
          Type : {block.type}
        </p>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPreview(block)}
          title="Aperçu"
        >
          <Eye className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggleActive(block)}
          title={block.is_active ? 'Désactiver' : 'Activer'}
        >
          <Switch checked={block.is_active} />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(block)}
          title="Modifier"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(block.id)}
          title="Supprimer"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

// Dialog Création
const CreateDialog = ({ open, onClose, form, setForm, templates, onCreate }) => {
  const getTemplateOptions = () => {
    return Object.keys(templates[form.type]).map(key => ({
      value: key,
      label: templates[form.type][key].title
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Créer un Bloc Global</DialogTitle>
          <DialogDescription>
            Choisissez un type et un template prédéfini
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Type de Bloc</Label>
            <Select 
              value={form.type} 
              onValueChange={(val) => setForm({...form, type: val, template: Object.keys(templates[val])[0]})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="header">Header</SelectItem>
                <SelectItem value="menu">Menu</SelectItem>
                <SelectItem value="footer">Footer</SelectItem>
                <SelectItem value="thematic_page_card">Carte Page Thématique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Template</Label>
            <Select 
              value={form.template} 
              onValueChange={(val) => setForm({...form, template: val})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getTemplateOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Titre (optionnel)</Label>
            <Input
              placeholder={templates[form.type][form.template]?.title || 'Titre'}
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>
          <Button onClick={onCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog Édition avec formulaire visuel
const EditDialog = ({ open, onClose, block, setBlock, onSave }) => {
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  // Charger les pages pour le sélecteur
  useEffect(() => {
    if (open && block?.type === 'thematic_page_card') {
      loadPages();
    }
  }, [open, block]);

  const loadPages = async () => {
    setLoadingPages(true);
    try {
      const response = await api.get('/pages');
      setPages(response.data || []);
    } catch (error) {
      console.error('Erreur chargement pages:', error);
    } finally {
      setLoadingPages(false);
    }
  };

  if (!block) return null;

  const updateContent = (key, value) => {
    setBlock({
      ...block,
      content: {
        ...block.content,
        [key]: value
      }
    });
  };

  const renderForm = () => {
    switch (block.type) {
      case 'header':
        return <HeaderForm content={block.content} updateContent={updateContent} />;
      case 'menu':
        return <MenuForm content={block.content} updateContent={updateContent} />;
      case 'footer':
        return <FooterForm content={block.content} updateContent={updateContent} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier : {block.title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Titre</Label>
            <Input
              value={block.title}
              onChange={(e) => setBlock({...block, title: e.target.value})}
            />
          </div>
          
          {renderForm()}
          
          {/* Padding Externe - UNIVERSEL */}
          <div className="border-t pt-4 mt-6">
            <ExternalPaddingControl
              value={block.content.external_padding || {}}
              onChange={(newPadding) => updateContent('external_padding', newPadding)}
            />
          </div>

          {/* Surface Variants */}
          <div className="border-t pt-4 space-y-4">
            <Label className="text-sm font-semibold text-muted-foreground">Surface (profondeur visuelle)</Label>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Fond</Label>
                <Select value={block.content.surface_bg || 'flat'} onValueChange={(v) => updateContent('surface_bg', v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Aucun</SelectItem>
                    <SelectItem value="soft-gradient">Dégradé doux</SelectItem>
                    <SelectItem value="radial-soft">Halo central</SelectItem>
                    <SelectItem value="subtle-pattern">Texture points</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contour</Label>
                <Select value={block.content.surface_border || 'none'} onValueChange={(v) => updateContent('surface_border', v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="subtle">Subtil</SelectItem>
                    <SelectItem value="accent">Accent chaud</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ombre</Label>
                <Select value={block.content.surface_shadow || 'none'} onValueChange={(v) => updateContent('surface_shadow', v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    <SelectItem value="soft">Douce</SelectItem>
                    <SelectItem value="elevated">Élevée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>
          <Button onClick={onSave}>
            <Save className="h-4 w-4 mr-2" />
            Sauvegarder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Formulaire Header
const HeaderForm = ({ content, updateContent }) => (
  <div className="space-y-4">
    <div>
      <Label>Nom du Site</Label>
      <Input
        value={content.site_name || ''}
        onChange={(e) => updateContent('site_name', e.target.value)}
        placeholder="Games Visor"
      />
    </div>
    
    <div>
      <Label>URL du Logo</Label>
      <Input
        value={content.logo_url || ''}
        onChange={(e) => updateContent('logo_url', e.target.value)}
        placeholder="https://..."
      />
    </div>
    
    <div className="flex items-center justify-between">
      <Label>Afficher la Recherche</Label>
      <Switch
        checked={content.show_search || false}
        onCheckedChange={(val) => updateContent('show_search', val)}
      />
    </div>
    
    <div className="flex items-center justify-between">
      <Label>Afficher Connexion</Label>
      <Switch
        checked={content.show_login || false}
        onCheckedChange={(val) => updateContent('show_login', val)}
      />
    </div>
    
    <div className="flex items-center justify-between">
      <Label>Afficher Inscription</Label>
      <Switch
        checked={content.show_signup || false}
        onCheckedChange={(val) => updateContent('show_signup', val)}
      />
    </div>
  </div>
);

// Formulaire Menu
const MenuForm = ({ content, updateContent }) => (
  <div className="space-y-4">
    <div>
      <Label>Style</Label>
      <Select
        value={content.style || 'horizontal'}
        onValueChange={(val) => updateContent('style', val)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="horizontal">Horizontal</SelectItem>
          <SelectItem value="vertical">Vertical</SelectItem>
        </SelectContent>
      </Select>
    </div>
    
    <div className="flex items-center justify-between">
      <Label>Afficher Icônes</Label>
      <Switch
        checked={content.show_icons || false}
        onCheckedChange={(val) => updateContent('show_icons', val)}
      />
    </div>
  </div>
);

// Formulaire Footer
const FooterForm = ({ content, updateContent }) => (
  <div className="space-y-4">
    <div>
      <Label>Nom du Site</Label>
      <Input
        value={content.site_name || ''}
        onChange={(e) => updateContent('site_name', e.target.value)}
      />
    </div>
    
    <div>
      <Label>URL du Logo</Label>
      <Input
        value={content.logo_url || ''}
        onChange={(e) => updateContent('logo_url', e.target.value)}
      />
    </div>
    
    <div>
      <Label>Tagline</Label>
      <Input
        value={content.tagline || ''}
        onChange={(e) => updateContent('tagline', e.target.value)}
      />
    </div>
    
    <div>
      <Label>Email</Label>
      <Input
        type="email"
        value={content.email || ''}
        onChange={(e) => updateContent('email', e.target.value)}
      />
    </div>
    
    <div>
      <Label>Téléphone</Label>
      <Input
        value={content.phone || ''}
        onChange={(e) => updateContent('phone', e.target.value)}
      />
    </div>
    
    <div>
      <Label>Adresse</Label>
      <Input
        value={content.address || ''}
        onChange={(e) => updateContent('address', e.target.value)}
      />
    </div>
  </div>
);

export default AdminGlobalBlocks;
